import { getLowestSupportedGradeLevel, getRoles, getSubjects } from './users';

export const COMPREHENSION = 'DOK.COMPREHENSION';

// LocalStorage key constants
export const LS_KEYS = {
  subject: 'skills::selectedSubject',
  subjectForQuestions: 'skills::selectedSubjectForQuestions',
  gradeLevel: 'skills::selectedGradeLevel',
  standardSet: 'skills::selectedStandardSet',
  course: 'skills::selectedCourse',
};

export const NON_STATE_STANDARD_SETS = {
  CCSS: { name: 'CCSS', set: 'CCSS' },
  NGSS: { name: 'NGSS', set: 'NGSS' },
  WIDA: { name: 'WIDA', set: 'WIDA' },
  ELPA21: { name: 'ELPA21', set: 'ELPA21' },
  DOK: { name: 'Depth of Knowledge', set: 'DOK' },
  GLS: { name: 'Literacy Skills', set: 'GLS' },
  AP: { name: 'AP', set: 'AP' },
};

export const STATE_STANDARD_SETS = {
  AL: { name: 'Alabama', set: 'AL' },
  AK: { name: 'Alaska', set: 'AK' },
  AZ: { name: 'Arizona', set: 'AZ' },
  AR: { name: 'Arkansas', set: 'AR' },
  CA: { name: 'California', set: 'CA' },
  CO: { name: 'Colorado', set: 'CO' },
  CT: { name: 'Connecticut', set: 'CT' },
  DE: { name: 'Delaware', set: 'DE' },
  FL: { name: 'Florida', set: 'FL' },
  GA: { name: 'Georgia', set: 'GA' },
  HI: { name: 'Hawaii', set: 'HI' },
  ID: { name: 'Idaho', set: 'ID' },
  IL: { name: 'Illinois', set: 'IL' },
  IN: { name: 'Indiana', set: 'IN' },
  IA: { name: 'Iowa', set: 'IA' },
  KS: { name: 'Kansas', set: 'KS' },
  KY: { name: 'Kentucky', set: 'KY' },
  LA: { name: 'Louisiana', set: 'LA' },
  ME: { name: 'Maine', set: 'ME' },
  MD: { name: 'Maryland', set: 'MD' },
  MA: { name: 'Massachusetts', set: 'MA' },
  MI: { name: 'Michigan', set: 'MI' },
  MN: { name: 'Minnesota', set: 'MN' },
  MS: { name: 'Mississippi', set: 'MS' },
  MO: { name: 'Missouri', set: 'MO' },
  MT: { name: 'Montana', set: 'MT' },
  NE: { name: 'Nebraska', set: 'NE' },
  NV: { name: 'Nevada', set: 'NV' },
  NH: { name: 'New Hampshire', set: 'NH' },
  NJ: { name: 'New Jersey', set: 'NJ' },
  NM: { name: 'New Mexico', set: 'NM' },
  NY: { name: 'New York', set: 'NY' },
  NC: { name: 'North Carolina', set: 'NC' },
  ND: { name: 'North Dakota', set: 'ND' },
  OH: { name: 'Ohio', set: 'OH' },
  OK: { name: 'Oklahoma', set: 'OK' },
  OR: { name: 'Oregon', set: 'OR' },
  PA: { name: 'Pennsylvania', set: 'PA' },
  RI: { name: 'Rhode Island', set: 'RI' },
  SC: { name: 'South Carolina', set: 'SC' },
  SD: { name: 'South Dakota', set: 'SD' },
  TN: { name: 'Tennessee', set: 'TN' },
  TX: { name: 'Texas', set: 'TX' },
  UT: { name: 'Utah', set: 'UT' },
  VT: { name: 'Vermont', set: 'VT' },
  VA: { name: 'Virginia', set: 'VA' },
  WA: { name: 'Washington', set: 'WA' },
  WV: { name: 'West Virginia', set: 'WV' },
  WI: { name: 'Wisconsin', set: 'WI' },
  WY: { name: 'Wyoming', set: 'WY' },
};

// AP courses (College Board). They share one subject entry: the course is chosen in the
// picker and supplies the subjectPath, so adding a course means adding one line here.
export const AP_COURSES = {
  AP_AFRICAN_AMERICAN_STUDIES: {
    name: 'AP African American Studies',
    subjectPath: 'AP African American Studies',
  },
  AP_ART_AND_DESIGN: {
    // one framework covers the 2-D, 3-D and Drawing portfolios
    name: 'AP Art and Design (2-D, 3-D, Drawing)',
    subjectPath: 'AP Art and Design',
  },
  AP_ART_HISTORY: { name: 'AP Art History', subjectPath: 'AP Art History' },
  AP_BIOLOGY: { name: 'AP Biology', subjectPath: 'AP Biology' },
  // AB and BC share one Course and Exam Description
  AP_CALCULUS: { name: 'AP Calculus AB/BC', subjectPath: 'AP Calculus AB and BC' },
  AP_CHEMISTRY: { name: 'AP Chemistry', subjectPath: 'AP Chemistry' },
  AP_CHINESE: {
    name: 'AP Chinese Language and Culture',
    subjectPath: 'AP Chinese Language and Culture',
  },
  AP_COMPARATIVE_GOVERNMENT: {
    name: 'AP Comparative Government and Politics',
    subjectPath: 'AP Comparative Government and Politics',
  },
  AP_COMPUTER_SCIENCE_A: { name: 'AP Computer Science A', subjectPath: 'AP Computer Science A' },
  AP_COMPUTER_SCIENCE_PRINCIPLES: {
    name: 'AP Computer Science Principles',
    subjectPath: 'AP Computer Science Principles',
  },
  AP_ENGLISH_LANGUAGE: {
    name: 'AP English Language and Composition',
    subjectPath: 'AP English Language and Composition',
  },
  AP_ENGLISH_LITERATURE: {
    name: 'AP English Literature and Composition',
    subjectPath: 'AP English Literature and Composition',
  },
  AP_ENVIRONMENTAL_SCIENCE: {
    name: 'AP Environmental Science',
    subjectPath: 'AP Environmental Science',
  },
  AP_EUROPEAN_HISTORY: { name: 'AP European History', subjectPath: 'AP European History' },
  AP_FRENCH: {
    name: 'AP French Language and Culture',
    subjectPath: 'AP French Language and Culture',
  },
  AP_GERMAN: {
    name: 'AP German Language and Culture',
    subjectPath: 'AP German Language and Culture',
  },
  AP_HUMAN_GEOGRAPHY: { name: 'AP Human Geography', subjectPath: 'AP Human Geography' },
  AP_ITALIAN: {
    name: 'AP Italian Language and Culture',
    subjectPath: 'AP Italian Language and Culture',
  },
  AP_JAPANESE: {
    name: 'AP Japanese Language and Culture',
    subjectPath: 'AP Japanese Language and Culture',
  },
  AP_LATIN: { name: 'AP Latin', subjectPath: 'AP Latin' },
  AP_MACROECONOMICS: { name: 'AP Macroeconomics', subjectPath: 'AP Macroeconomics' },
  AP_MICROECONOMICS: { name: 'AP Microeconomics', subjectPath: 'AP Microeconomics' },
  AP_MUSIC_THEORY: { name: 'AP Music Theory', subjectPath: 'AP Music Theory' },
  AP_PHYSICS_1: { name: 'AP Physics 1', subjectPath: 'AP Physics 1' },
  AP_PHYSICS_2: { name: 'AP Physics 2', subjectPath: 'AP Physics 2' },
  // ':' and '/' are avoided in subjectPath: it is a directory name and a CDN path segment
  AP_PHYSICS_C_EM: {
    name: 'AP Physics C: Electricity and Magnetism',
    subjectPath: 'AP Physics C - Electricity and Magnetism',
  },
  AP_PHYSICS_C_MECHANICS: {
    name: 'AP Physics C: Mechanics',
    subjectPath: 'AP Physics C - Mechanics',
  },
  AP_PRECALCULUS: { name: 'AP Precalculus', subjectPath: 'AP Precalculus' },
  AP_PSYCHOLOGY: { name: 'AP Psychology', subjectPath: 'AP Psychology' },
  AP_RESEARCH: { name: 'AP Research', subjectPath: 'AP Research' },
  AP_SEMINAR: { name: 'AP Seminar', subjectPath: 'AP Seminar' },
  AP_SPANISH: {
    name: 'AP Spanish Language and Culture',
    subjectPath: 'AP Spanish Language and Culture',
  },
  AP_SPANISH_LITERATURE: {
    name: 'AP Spanish Literature and Culture',
    subjectPath: 'AP Spanish Literature and Culture',
  },
  AP_STATISTICS: { name: 'AP Statistics', subjectPath: 'AP Statistics' },
  AP_US_GOVERNMENT: {
    name: 'AP U.S. Government and Politics',
    subjectPath: 'AP U.S. Government and Politics',
  },
  AP_US_HISTORY: { name: 'AP U.S. History', subjectPath: 'AP U.S. History' },
  AP_WORLD_HISTORY: { name: 'AP World History: Modern', subjectPath: 'AP World History - Modern' },
};

export const STANDARD_SET_MAP = {
  ...NON_STATE_STANDARD_SETS,
  ...STATE_STANDARD_SETS,
};

export const SUBJECT_MAP = {
  ENGLISH_LANGUAGE_ARTS: {
    name: 'ELA Standards (State)',
    subjectPath: 'English Language Arts',
    standardType: 'language',
    isPremium: true,
  },
  CCSS_ENGLISH_LANGUAGE_ARTS: {
    name: 'ELA Standards (CCSS)',
    subjectPath: 'English Language Arts',
    standardType: 'language',
    setOverride: 'CCSS',
    isPremium: true,
  },
  SCIENCE: {
    name: 'Science Standards (State)',
    subjectPath: 'Science',
    standardType: 'content',
    isPremium: true,
  },
  NGSS_SCIENCE: {
    name: 'Science Standards (NGSS)',
    subjectPath: 'Science',
    standardType: 'content',
    setOverride: 'NGSS',
    isPremium: true,
  },
  SOCIAL_STUDIES: {
    name: 'Social Studies Standards',
    subjectPath: 'Social Studies',
    standardType: 'content',
    isPremium: true,
  },
  MATH: {
    name: 'Math Standards (State)',
    subjectPath: 'Math',
    standardType: 'content',
    isPremium: true,
  },
  CCSS_MATH: {
    name: 'Math Standards (CCSS)',
    subjectPath: 'Math',
    standardType: 'content',
    setOverride: 'CCSS',
    isPremium: true,
  },
  ENGLISH_LANGUAGE_DEVELOPMENT: {
    name: 'ELD Standards',
    subjectPath: 'English Language Development',
    standardType: 'language',
    isPremium: true,
  },
  WIDA: {
    name: 'WIDA Framework',
    subjectPath: 'English Language Development',
    standardType: 'language',
    setOverride: 'WIDA',
    isPremium: true,
    skipSorting: true,
  },
  ELPA21: {
    name: 'ELPA21 Standards',
    subjectPath: 'English Language Development',
    standardType: 'language',
    setOverride: 'ELPA21',
    isPremium: true,
    skipSorting: true,
  },
  DOK: {
    name: 'Depth of Knowledge',
    subjectPath: 'Depth of Knowledge',
    standardType: 'language',
    setOverride: 'DOK',
    isPremium: false,
    skipSorting: true,
  },
  LITERACY_SKILLS: {
    name: 'Literacy Skills',
    subjectPath: 'Literacy Skills',
    standardType: 'language',
    setOverride: 'GLS',
    isPremium: true,
    skipSorting: true,
  },
  FOREIGN_LANGUAGE: {
    name: 'World Language Standards',
    subjectPath: 'Foreign Language',
    standardType: 'language',
    isPremium: true,
  },
  CAREER_EDUCATION: {
    name: 'Career Education Standards',
    subjectPath: 'Career Education',
    standardType: 'content',
    isPremium: true,
  },
  THE_ARTS: {
    name: 'The Arts Standards',
    subjectPath: 'The Arts',
    standardType: 'content',
    isPremium: true,
  },
  PHYSICAL_EDUCATION: {
    name: 'Physical Education Standards',
    subjectPath: 'Physical Education',
    standardType: 'content',
    isPremium: true,
  },
  SOCIAL_EMOTIONAL_LEARNING: {
    name: 'SEL Standards',
    subjectPath: 'Social Emotional Learning',
    standardType: 'content',
    isPremium: true,
  },
  LIFE_SKILLS: {
    name: 'Life Skills',
    subjectPath: 'Life Skills',
    standardType: 'content',
    isPremium: true,
  },
  HEALTH: {
    name: 'Health Standards',
    subjectPath: 'Health',
    standardType: 'content',
    isPremium: true,
  },
  TECHNOLOGY: {
    name: 'Technology Standards',
    subjectPath: 'Technology',
    standardType: 'content',
    isPremium: true,
  },
  FINANCIAL_LITERACY: {
    name: 'Financial Literacy Standards',
    subjectPath: 'Financial Literacy',
    standardType: 'content',
    isPremium: true,
  },
  AP_STANDARDS: {
    name: 'AP Standards',
    // subjectPath comes from the selected course, see AP_COURSES
    courses: AP_COURSES,
    // AP courses are not grade-specific: one file covers the whole band, and the picker
    // hides its grade control
    gradeBand: '9-12',
    standardType: 'content',
    setOverride: 'AP',
    isPremium: true,
    // keep CED order: each unit's topics, and each objective followed by its essential
    // knowledge. Sorting by id would split the objectives from the statements under them.
    skipSorting: true,
  },
};

export const GRADE_LEVEL_MAP = {
  0: { name: 'Kindergarten', gradeLevel: 'Kindergarten', readingLevel: '0' },
  1: { name: '1st Grade', gradeLevel: '1', readingLevel: '1' },
  2: { name: '2nd Grade', gradeLevel: '2', readingLevel: '2' },
  3: { name: '3rd Grade', gradeLevel: '3', readingLevel: '3' },
  4: { name: '4th Grade', gradeLevel: '4', readingLevel: '4' },
  5: { name: '5th Grade', gradeLevel: '5', readingLevel: '5' },
  6: { name: '6th Grade', gradeLevel: '6', readingLevel: '6' },
  7: { name: '7th Grade', gradeLevel: '7', readingLevel: '7' },
  8: { name: '8th Grade', gradeLevel: '8', readingLevel: '8' },
  9: { name: '9th Grade', gradeLevel: '9', readingLevel: '9' },
  10: { name: '10th Grade', gradeLevel: '10', readingLevel: '10' },
  11: { name: '11th Grade', gradeLevel: '11', readingLevel: '11' },
  12: { name: '12th Grade', gradeLevel: '12', readingLevel: '12' },
};

export const getGradeLevel = (diffitUser) => {
  // first: check the cache
  let cachedGradeLevelId = skillsStorage.getGradeLevel();
  if (!!cachedGradeLevelId) {
    return GRADE_LEVEL_MAP[cachedGradeLevelId];
  }

  // second: if there is a user, check to see if the user has a grade level
  const lowestUserGradeLevelId = getLowestSupportedGradeLevel(diffitUser);

  if (lowestUserGradeLevelId) {
    skillsStorage.setGradeLevel(lowestUserGradeLevelId);
    return GRADE_LEVEL_MAP[lowestUserGradeLevelId];
  }

  // last: if all else fails, return the default grade level (5th Grade)
  return GRADE_LEVEL_MAP[5];
};

// Helper: get the grade level key (string) rather than the object
export const getGradeLevelKey = (diffitUser) => {
  const cached = skillsStorage.getGradeLevel();
  if (cached) return cached;
  const lowest = getLowestSupportedGradeLevel(diffitUser);
  const key = lowest != null ? String(lowest) : '5';
  skillsStorage.setGradeLevel(key);
  return key;
};

export const getStandardSet = (diffitUser) => {
  let cachedStandardSet = skillsStorage.getStandardSet();
  if (!!cachedStandardSet) {
    return cachedStandardSet;
  }
  let state = diffitUser?.school_records?.[0]?.state || '';
  skillsStorage.setStandardSet(state);
  return state;
};

export const getSubject = () => {
  let cachedSubject = skillsStorage.getSubject() || '';
  if (!!cachedSubject) {
    return cachedSubject;
  }
  return '';
};

export const getSubjectForQuestions = () => {
  // Check for a cached subject for questions (separate from packet subject)
  let cachedSubjectForQuestions = skillsStorage.getSubjectForQuestions() || '';
  if (!!cachedSubjectForQuestions) {
    return cachedSubjectForQuestions;
  }

  // If no cached subject for questions, check if there's a cached subject we can use
  let cachedSubject = skillsStorage.getSubject() || '';
  if (cachedSubject && SUBJECT_MAP[cachedSubject]) {
    // If the cached subject is a language type, we can use it as the subject for questions
    if (SUBJECT_MAP[cachedSubject].standardType === 'language') {
      return cachedSubject;
    }
    // If it's a content type, default to DOK for questions
    return 'DOK';
  }

  // Return DOK as the default subject for questions
  return 'DOK';
};

// Centralized storage helpers
export const skillsStorage = {
  getSubject: () => localStorage.getItem(LS_KEYS.subject) || '',
  setSubject: (key) => localStorage.setItem(LS_KEYS.subject, key),
  getGradeLevel: () => localStorage.getItem(LS_KEYS.gradeLevel),
  setGradeLevel: (key) => localStorage.setItem(LS_KEYS.gradeLevel, key),
  getStandardSet: () => localStorage.getItem(LS_KEYS.standardSet) || '',
  setStandardSet: (key) => localStorage.setItem(LS_KEYS.standardSet, key),
  getSubjectForQuestions: () => localStorage.getItem(LS_KEYS.subjectForQuestions) || '',
  setSubjectForQuestions: (key) => localStorage.setItem(LS_KEYS.subjectForQuestions, key),
  getCourse: () => localStorage.getItem(LS_KEYS.course) || '',
  setCourse: (key) => localStorage.setItem(LS_KEYS.course, key),
};

function toHumanTitleCase(text) {
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getHumanReadableSkillFromID(skill_id) {
  if (!skill_id) return null;
  // Handle Literacy Skills and Depth of Knowledge
  if (skill_id.startsWith('LS.') || skill_id.startsWith('DOK.')) {
    const literacySkill = skill_id.split('.')[1];
    return toHumanTitleCase(literacySkill);
  }
  // Handle English Language Development
  if (skill_id.startsWith('ELD-')) {
    const parts = skill_id.split('.');
    const prefix = parts[0]; // e.g., "ELD-SI"

    // Remove second (gade level) and last part (id number), keep middle parts if any
    const middleParts = parts.slice(2, -1);

    if (middleParts.length > 0) {
      // Capitalize and join middle parts with spaces
      const formattedMiddle = middleParts
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
      return `${prefix}.${formattedMiddle}`;
    }

    return prefix;
  }
  // Catch all for other custom skills (real standards use ".")
  if (skill_id.includes('_')) {
    return toHumanTitleCase(skill_id);
  }
  return skill_id;
}

// get the human readable name for a skill or standard (including focus if present)
export const getPillNameFromSkillOrStandard = (skillOrStandard) => {
  const baseName = getHumanReadableSkillFromID(skillOrStandard?.id);
  if (skillOrStandard?.focus && skillOrStandard.focus.trim()) {
    const truncatedFocus =
      skillOrStandard.focus.length > 20
        ? skillOrStandard.focus.substring(0, 20) + '...'
        : skillOrStandard.focus;
    return `${baseName}: ${truncatedFocus}`;
  }
  return baseName;
};

export const isComprehensionSkill = (skill) => {
  return skill?.id === 'DOK.COMPREHENSION';
};

// Filter SUBJECT_MAP by standardType
export const getSubjectMapByStandardType = (standardType) => {
  return Object.keys(SUBJECT_MAP).reduce((filtered, key) => {
    if (SUBJECT_MAP[key].standardType === standardType) {
      filtered[key] = SUBJECT_MAP[key];
    }
    return filtered;
  }, {});
};

// Pre-filtered objects for common use cases
export const LANGUAGE_SUBJECTS = getSubjectMapByStandardType('language');
export const CONTENT_SUBJECTS = getSubjectMapByStandardType('content');
export const SUBJECT_FOR_QUESTIONS_OPTIONS = {
  ...getSubjectMapByStandardType('language'),
  CUSTOM: { name: 'Custom', isPremium: true },
};

// Build subject-for-questions options list with centralized filtering
export const buildSubjectForQuestionsOptions = (isStateSetSelected, questionType) => {
  let options = Object.entries(SUBJECT_FOR_QUESTIONS_OPTIONS).map(([key, cfg]) => ({
    name: cfg.name,
    value: key,
    premium: !!cfg.isPremium,
  }));

  // When no state is selected, hide state-only ELA and ELD
  if (!isStateSetSelected) {
    options = options.filter(
      (opt) => !['ENGLISH_LANGUAGE_ARTS', 'ENGLISH_LANGUAGE_DEVELOPMENT'].includes(opt.value)
    );
  }

  // For open-ended questions, hide ELA (state & CCSS) and Literacy Skills
  if (questionType === 'oeq') {
    options = options.filter(
      (opt) =>
        ![
          'ENGLISH_LANGUAGE_ARTS',
          'CCSS_ENGLISH_LANGUAGE_ARTS',
          'LITERACY_SKILLS',
          'ENGLISH_LANGUAGE_DEVELOPMENT',
          'FOREIGN_LANGUAGE',
          'WIDA',
          'ELPA21',
        ].includes(opt.value)
    );
  }

  return options;
};

// Subjects whose standards are split into courses (AP) resolve their CDN path from the
// selected course; every other subject has a fixed subjectPath.
export const getSubjectCourses = (subjectKey) => SUBJECT_MAP[subjectKey]?.courses || null;

export const getDefaultCourseKey = (subjectKey) => {
  const courses = getSubjectCourses(subjectKey);
  if (!courses) return '';
  const cached = skillsStorage.getCourse();
  return cached && courses[cached] ? cached : Object.keys(courses)[0];
};

export const resolveSubjectPath = (subjectKey, courseKey) => {
  const config = SUBJECT_MAP[subjectKey];
  if (!config) return undefined;
  return config.courses ? config.courses[courseKey]?.subjectPath : config.subjectPath;
};

// Grade-agnostic subjects (AP) are served as one band file rather than per grade
export const resolveGradeToken = (subjectKey, gradeKey) =>
  SUBJECT_MAP[subjectKey]?.gradeBand || GRADE_LEVEL_MAP[gradeKey]?.gradeLevel;

export const isGradeAgnosticSubject = (subjectKey) => !!SUBJECT_MAP[subjectKey]?.gradeBand;

// check if a subject is state-specific
// State-specific subjects are those that don't have a setOverride property
// Subjects with setOverride use national standards (CCSS, NGSS, WIDA, DOK, GLS)
export const isStateSpecificSubject = (subjectKey) => {
  const config = SUBJECT_MAP[subjectKey];
  if (!config) return false;

  // If the subject has a setOverride, it uses a national standard set
  // Otherwise, it requires a state to be selected
  return !config.setOverride;
};

// determine if subject requires a state selection (no override and no state key)?
export const requiresStateSelection = (subjectKey, standardSetKey) => {
  return isStateSpecificSubject(subjectKey) && !STATE_STANDARD_SETS[standardSetKey];
};

// Map user profile subjects (from onboarding) to SUBJECT_MAP keys
// Returns an array of keys since some subjects map to multiple standards
// When no state is selected, only returns national standards
export const mapUserSubjectsToSubjectKeys = (userSubjects, hasStateSelected = true) => {
  // Define all possible mappings
  const allMappings = {
    'English Language Arts': ['ENGLISH_LANGUAGE_ARTS', 'CCSS_ENGLISH_LANGUAGE_ARTS'],
    'Social Studies': ['SOCIAL_STUDIES'],
    Science: ['SCIENCE', 'NGSS_SCIENCE'],
    Math: ['MATH', 'CCSS_MATH'],
    'English Language Learners': ['ENGLISH_LANGUAGE_DEVELOPMENT', 'WIDA', 'ELPA21'],
    'Special Education': ['LITERACY_SKILLS'],
    'World Language': ['FOREIGN_LANGUAGE'],
    'Career and Technical Education': ['CAREER_EDUCATION', 'TECHNOLOGY'],
    Arts: ['THE_ARTS'],
  };

  const subjectKeys = new Set(userSubjects.flatMap((subject) => allMappings[subject] || []));

  // If no state is selected, filter out state-specific subjects
  if (!hasStateSelected) {
    return new Set([...subjectKeys].filter((key) => !isStateSpecificSubject(key)));
  }

  return subjectKeys;
};

export const mapUserRolesToSubjectKeys = (userRoles, hasStateSelected = true) => {
  const allMappings = {
    intervention_specialist_multilingual_learners: [
      'ENGLISH_LANGUAGE_DEVELOPMENT',
      'WIDA',
      'ELPA21',
    ],
  };

  const roleKeys = new Set(userRoles.flatMap((role) => allMappings[role] || []));

  if (!hasStateSelected) {
    return new Set([...roleKeys].filter((key) => !isStateSpecificSubject(key)));
  }

  return roleKeys;
};

// Determine effective subject key based on mode/filters
export const determineEffectiveSubjectKey = (filters, mode) => {
  return mode === 'addQuestion' && filters.subjectForQuestionsKey
    ? filters.subjectForQuestionsKey
    : filters.subjectKey;
};

// Constrain subject for questions when no user state is selected
export const constrainSubjectForQuestionsAgainstState = (subjectKey, userStateKey) => {
  const hasStateSelected = !!userStateKey && !!STATE_STANDARD_SETS[userStateKey];
  if (hasStateSelected) return subjectKey;
  if (['ENGLISH_LANGUAGE_ARTS', 'ENGLISH_LANGUAGE_DEVELOPMENT'].includes(subjectKey)) {
    return 'CCSS_ENGLISH_LANGUAGE_ARTS' in SUBJECT_MAP ? 'CCSS_ENGLISH_LANGUAGE_ARTS' : 'DOK';
  }
  return subjectKey;
};

// Constrain subject for questions when the question type has restrictions
// For open-ended questions (oeq), disallow ELA (state and CCSS) and Literacy Skills
export const constrainSubjectForQuestionsAgainstQuestionType = (subjectKey, questionType) => {
  if (questionType === 'oeq') {
    const disallowed = new Set([
      'ENGLISH_LANGUAGE_ARTS',
      'CCSS_ENGLISH_LANGUAGE_ARTS',
      'LITERACY_SKILLS',
      'ENGLISH_LANGUAGE_DEVELOPMENT',
      'FOREIGN_LANGUAGE',
      'WIDA',
      'ELPA21',
    ]);
    if (disallowed.has(subjectKey)) {
      return 'DOK';
    }
  }
  return subjectKey;
};

// Compose all constraints for subject-for-questions selection in one place
export const constrainSubjectForQuestions = (subjectKey, { userStateKey, questionType } = {}) => {
  let adjusted = constrainSubjectForQuestionsAgainstState(subjectKey, userStateKey);
  adjusted = constrainSubjectForQuestionsAgainstQuestionType(adjusted, questionType);
  return adjusted;
};

// organize subjects into "Recommended" and "All Standards" categories
export const getCategorizedSubjects = (diffitUser, isLoggedIn) => {
  const cachedSubject = skillsStorage.getSubject() || '';
  const userSubjects = getSubjects(diffitUser);
  const userRoles = getRoles(diffitUser);
  // Check if a state is selected
  const selectedState = getStandardSet(diffitUser);
  const hasStateSelected = !!selectedState && !!STATE_STANDARD_SETS[selectedState];

  const recommended = [];
  const allStandards = [];
  const usedKeys = new Set();

  // Add cached subject first if it exists
  // Skip state-specific subjects if no state is selected
  if (cachedSubject && SUBJECT_MAP[cachedSubject]) {
    const subjectConfig = SUBJECT_MAP[cachedSubject];

    if (!isStateSpecificSubject(cachedSubject) || hasStateSelected) {
      recommended.push({
        name: subjectConfig.name,
        value: cachedSubject,
        isPremium: subjectConfig.isPremium,
      });
      usedKeys.add(cachedSubject);
    }
  }

  // Add user's profile subjects if logged in (these come before DOK/Literacy Skills)
  if (isLoggedIn && userSubjects?.length > 0) {
    const subjectKeys = mapUserSubjectsToSubjectKeys(userSubjects, hasStateSelected);
    subjectKeys.forEach((subjectKey) => {
      if (SUBJECT_MAP[subjectKey] && !usedKeys.has(subjectKey)) {
        recommended.push({
          name: SUBJECT_MAP[subjectKey].name,
          value: subjectKey,
          isPremium: SUBJECT_MAP[subjectKey].isPremium,
        });
        usedKeys.add(subjectKey);
      }
    });
  }

  // Add user's profile roles if logged in
  if (isLoggedIn && userRoles?.length > 0) {
    const roleKeys = mapUserRolesToSubjectKeys(userRoles, hasStateSelected);
    roleKeys.forEach((roleKey) => {
      if (SUBJECT_MAP[roleKey] && !usedKeys.has(roleKey)) {
        recommended.push({
          name: SUBJECT_MAP[roleKey].name,
          value: roleKey,
          isPremium: SUBJECT_MAP[roleKey].isPremium,
        });
        usedKeys.add(roleKey);
      }
    });
  }

  // Then add DOK and Literacy Skills to recommended
  if (!usedKeys.has('DOK')) {
    recommended.push({
      name: SUBJECT_MAP.DOK.name,
      value: 'DOK',
      isPremium: SUBJECT_MAP.DOK.isPremium,
    });
    usedKeys.add('DOK');
  }

  if (!usedKeys.has('LITERACY_SKILLS')) {
    recommended.push({
      name: SUBJECT_MAP.LITERACY_SKILLS.name,
      value: 'LITERACY_SKILLS',
      isPremium: SUBJECT_MAP.LITERACY_SKILLS.isPremium,
    });
    usedKeys.add('LITERACY_SKILLS');
  }

  // Add remaining subjects to "All Standards" in alphabetical order
  const remainingSubjects = Object.entries(SUBJECT_MAP)
    .filter(([key]) => !usedKeys.has(key))
    .map(([key, config]) => ({
      name: config.name,
      value: key,
      isPremium: config.isPremium,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  allStandards.push(...remainingSubjects);

  // Build the final list with category headers
  const categorizedList = [];

  if (recommended.length > 0) {
    categorizedList.push({ name: 'Recommended', isCategory: true });
    categorizedList.push(...recommended);
  }

  if (allStandards.length > 0) {
    categorizedList.push({ name: 'All Standards', isCategory: true });
    categorizedList.push(...allStandards);
  }

  return categorizedList;
};

export const getPacketStandards = (() => {
  const TYPES = ['mcq', 'saq', 'oeq'];
  return (packet) => {
    if (!packet) return [];
    const m = new Map();
    const add = (s) => {
      if (s && s.id != null && !m.has(s.id)) m.set(s.id, s);
    };

    const ls = packet.language_standards_json;
    if (Array.isArray(ls)) for (let i = 0; i < ls.length; i++) add(ls[i]);

    const cs = packet.content_standards_json;
    if (Array.isArray(cs)) {
      for (let i = 0; i < cs.length; i++) add(cs[i]);
    } else if (cs) {
      add(cs);
    }

    const blocks = packet.blocks;
    if (Array.isArray(blocks)) {
      for (let b = 0; b < blocks.length; b++) {
        const rd = blocks[b] && blocks[b].resource_data;
        if (!rd) continue;
        for (let t = 0; t < TYPES.length; t++) {
          const items = rd[TYPES[t]] && rd[TYPES[t]].items;
          if (!Array.isArray(items)) continue;
          for (let j = 0; j < items.length; j++) add(items[j] && items[j].language_standard);
        }
      }
    }
    return Array.from(m.values());
  };
})();
